import { Audit as alfaAudit, Outcome } from "@siteimprove/alfa-act";
import { Node as ariaNode } from "@siteimprove/alfa-aria";
import { Cascade } from "@siteimprove/alfa-cascade";
import { Map } from "@siteimprove/alfa-map";
import { Performance as alfaPerformance } from "@siteimprove/alfa-performance";
import { alfaVersion } from "@siteimprove/alfa-rules";
import { Sequence } from "@siteimprove/alfa-sequence";
import * as json from "@siteimprove/alfa-json";
import { Outcomes } from "./outcomes.js";
import { Performance } from "./performance.js";
import { Rules } from "./rules.js";
/**
 * An Alfa Audit
 *
 * @public
 */
export class Audit {
    static of(page, outcomes, resultAggregates, durations) {
        return new Audit(page, outcomes, resultAggregates, durations);
    }
    _alfaVersion;
    _page;
    _outcomes;
    _resultAggregates;
    _durations;
    constructor(page, outcomes, resultAggregates, durations) {
        this._alfaVersion = alfaVersion;
        this._page = page;
        this._outcomes = outcomes;
        this._resultAggregates = resultAggregates;
        this._durations = durations;
    }
    /**
     * The version of Alfa used to run the audit.
     */
    get alfaVersion() {
        return this._alfaVersion;
    }
    /**
     * The audited page (Alfa representation).
     */
    get page() {
        return this._page;
    }
    /**
     * The audit outcomes, sorted by rule.
     */
    get outcomes() {
        return this._outcomes;
    }
    /**
     * Aggregated result per rule
     */
    get resultAggregates() {
        return this._resultAggregates;
    }
    /**
     * Performance durations for the audit.
     */
    get durations() {
        return this._durations;
    }
    toJSON() {
        return {
            alfaVersion: this._alfaVersion,
            page: this._page.toJSON({ verbosity: json.Serializable.Verbosity.High }),
            outcomes: Sequence.from(this._outcomes.values()).flatten().toJSON({
                verbosity: json.Serializable.Verbosity.Minimal,
            }),
            resultAggregates: this._resultAggregates.toJSON(),
            durations: this._durations,
        };
    }
}
/**
 * An Alfa Audit
 *
 * @public
 */
(function (Audit) {
    function isAudit(value) {
        return value instanceof Audit;
    }
    Audit.isAudit = isAudit;
    /**
     * Audit a given page. Use the filters to select the rules to run, and
     * the outcomes to keep.
     */
    async function run(page, options = {}) {
        const durations = Performance.empty();
        const commonPerformance = Performance.recordCommon(durations);
        const start = commonPerformance.mark("total").start;
        sharedPerformance(commonPerformance, page);
        const rulesToRun = options.rules?.override ?? false
            ? options.rules?.custom ?? []
            : filter(Rules.allRules, options.rules).concat(options.rules?.custom ?? []);
        const audit = Sequence.from(await alfaAudit.of(page, rulesToRun).evaluate());
        commonPerformance.measure("total", start);
        const outcomes = filter(audit, options.outcomes)
            .reject(options?.outcomes?.includeIFrames ?? false
            ? none
            : Outcomes.insideSelectorFilter("iframe"))
            .groupBy((outcome) => outcome.rule.uri);
        const resultAggregates = outcomes
            // For each rule, group by outcome
            .map((ruleOutcomes) => ruleOutcomes.groupBy((outcome) => outcome.outcome))
            // Count the size of each group and build the aggregates
            .map((groups, uri) => ({
            failed: groups.get(Outcome.Value.Failed).getOrElse(Sequence.empty).size,
            passed: groups.get(Outcome.Value.Passed).getOrElse(Sequence.empty).size,
            cantTell: groups.get(Outcome.Value.CantTell).getOrElse(Sequence.empty)
                .size,
        }));
        return Audit.of(page, outcomes, resultAggregates, durations);
    }
    Audit.run = run;
    const all = () => true;
    const none = () => false;
    /**
     * @internal
     */
    function filter(sequence, filter = {}) {
        return sequence
            .filter(filter.include ?? all)
            .reject(filter.exclude ?? none);
    }
    Audit.filter = filter;
    /**
     * Resolve Cascade and build the ARIA tree, while recording performance.
     *
     * @remarks
     * This ensures that these costly but cached operations are "charged" separately
     * and not unfairly on the first rule using them.
     */
    function sharedPerformance(performance, page) {
        const startCascade = performance.mark("cascade").start;
        Cascade.from(page.document, page.device);
        performance.measure("cascade", startCascade);
        const startAria = performance.mark("aria-tree").start;
        ariaNode.from(page.document, page.device);
        performance.measure("aria-tree", startAria);
    }
})(Audit || (Audit = {}));
//# sourceMappingURL=audit.js.map